import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { errorMessage, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { ArrowLeft, Loader2, Eye, Lock } from "lucide-react";
import { toast } from "sonner";
import { formatRupiah, formatTanggal } from "../lib/format";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

export default function InvoiceCreatePage() {
    const nav = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [vendors, setVendors] = useState([]);
    const [vendorId, setVendorId] = useState("");
    const [tanggalMulai, setTanggalMulai] = useState(monthStart());
    const [tanggalAkhir, setTanggalAkhir] = useState(today());
    const [dicekOleh, setDicekOleh] = useState("YUNITA");
    const [disetujuiOleh, setDisetujuiOleh] = useState("RAJAYA");
    const [catatanAdmin, setCatatanAdmin] = useState("");
    const [carryOver, setCarryOver] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [confirmTutup, setConfirmTutup] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { (async () => { try { const v = await api.get("/vendors"); setVendors(v.data); } catch (e) { toast.error(errorMessage(e)); } })(); }, []);

    const doPreview = async () => {
        if (!vendorId) return toast.error("Pilih vendor dulu");
        setLoadingPreview(true);
        try {
            const { data } = await api.post("/invoice/preview", { vendor_id: vendorId, tanggal_mulai: tanggalMulai, tanggal_akhir: tanggalAkhir });
            setPreview(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoadingPreview(false); }
    };

    const doTutup = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post("/invoice", {
                vendor_id: vendorId, tanggal_mulai: tanggalMulai, tanggal_akhir: tanggalAkhir,
                dicek_oleh: dicekOleh, disetujui_oleh: disetujuiOleh,
                catatan_admin: catatanAdmin, carry_over_ke_invoice_berikutnya: carryOver,
            });
            toast.success(`Invoice ${data.nomor_invoice} berhasil ditutup`);
            setConfirmTutup(false);
            nav(`/invoice/${data.id}`);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const totalPeng = preview ? (preview.total_bon_kain + preview.total_bon_peralatan + preview.total_kasbon + preview.total_potongan_reject) : 0;
    const totalPem = preview ? (preview.total_setor + preview.total_kredit_reject_selesai + preview.saldo_awal) : 0;

    return (
        <div className="space-y-5">
            <div className="flex items-center gap-3">
                <button onClick={() => nav("/invoice")} className="p-2 rounded-xl hover:bg-slate-100 text-slate-600" data-testid="invoice-back-btn"><ArrowLeft className="w-4 h-4" /></button>
                <div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Buat Invoice Baru</h2>
                    <p className="text-sm text-slate-500 mt-1">Pilih vendor + rentang tanggal, preview sebelum tutup permanen.</p>
                </div>
            </div>

            <div className="glass-card rounded-2xl p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Label>Vendor <span className="text-rose-600">*</span></Label>
                        <Select value={vendorId} onValueChange={setVendorId}>
                            <SelectTrigger data-testid="invoice-form-vendor" className="mt-1.5 rounded-xl"><SelectValue placeholder="Pilih vendor" /></SelectTrigger>
                            <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.nama}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Tanggal Mulai</Label>
                        <Input type="date" value={tanggalMulai} onChange={(e) => setTanggalMulai(e.target.value)} data-testid="invoice-form-tanggal-mulai" className="mt-1.5 rounded-xl" />
                    </div>
                    <div>
                        <Label>Tanggal Akhir</Label>
                        <Input type="date" value={tanggalAkhir} onChange={(e) => setTanggalAkhir(e.target.value)} data-testid="invoice-form-tanggal-akhir" className="mt-1.5 rounded-xl" />
                    </div>
                </div>
                <div className="flex justify-end">
                    <Button onClick={doPreview} disabled={loadingPreview || !vendorId} data-testid="invoice-preview-btn" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl gap-2">
                        {loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview Invoice
                    </Button>
                </div>
            </div>

            {preview && (
                <div className="glass-card rounded-2xl p-5 space-y-4">
                    <div className="pb-4 border-b border-slate-200">
                        <div className="text-lg font-bold text-slate-900">Preview Invoice</div>
                        <div className="text-sm text-slate-500 mt-1">Vendor: <span className="font-semibold text-slate-900">{preview.vendor.nama}</span> · Periode {formatTanggal(preview.tanggal_mulai)} — {formatTanggal(preview.tanggal_akhir)}</div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-rose-700 mb-2">Pengeluaran Vendor</div>
                        <div className="space-y-1.5 text-sm">
                            <div className="flex justify-between"><span className="text-slate-700">Bon Kain <span className="text-slate-400 text-xs">({preview.rincian.bon_kain.length} tx)</span></span><span className="font-semibold num-tabular text-slate-900">{formatRupiah(preview.total_bon_kain)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-700">Bon Peralatan <span className="text-slate-400 text-xs">({preview.rincian.bon_peralatan.length} tx)</span></span><span className="font-semibold num-tabular text-slate-900">{formatRupiah(preview.total_bon_peralatan)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-700">Kasbon <span className="text-slate-400 text-xs">({preview.rincian.kasbon.length} tx)</span></span><span className="font-semibold num-tabular text-slate-900">{formatRupiah(preview.total_kasbon)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-700">Potongan Reject Permanen + Expired</span><span className="font-semibold num-tabular text-slate-900">{formatRupiah(preview.total_potongan_reject)}</span></div>
                            <div className="flex justify-between pt-2 mt-2 border-t border-slate-200"><span className="font-semibold text-slate-900">SUBTOTAL PENGELUARAN</span><span className="font-bold num-tabular text-rose-700">{formatRupiah(totalPeng)}</span></div>
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">Pemasukan Vendor</div>
                        <div className="space-y-1.5 text-sm">
                            <div className="flex justify-between"><span className="text-slate-700">Setor Barang Jadi <span className="text-slate-400 text-xs">({preview.rincian.setor_barang.length} tx)</span></span><span className="font-semibold num-tabular text-slate-900">{formatRupiah(preview.total_setor)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-700">Kredit Reject Selesai Diperbaiki <span className="text-slate-400 text-xs">({preview.rincian.reject_selesai_kredit.length} pcs)</span></span><span className="font-semibold num-tabular text-slate-900">{formatRupiah(preview.total_kredit_reject_selesai)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-700">Saldo Awal (periode sebelumnya)</span><span className={`font-semibold num-tabular ${preview.saldo_awal < 0 ? "text-rose-700" : "text-slate-900"}`}>{formatRupiah(preview.saldo_awal)}</span></div>
                            <div className="flex justify-between pt-2 mt-2 border-t border-slate-200"><span className="font-semibold text-slate-900">SUBTOTAL PEMASUKAN</span><span className="font-bold num-tabular text-emerald-700">{formatRupiah(totalPem)}</span></div>
                        </div>
                    </div>

                    <div data-testid="invoice-preview-total" className={`p-4 rounded-xl ${preview.total_penghasilan_vendor >= 0 ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold uppercase tracking-wider text-slate-700">Penghasilan Vendor</span>
                            <span className={`text-2xl font-extrabold num-tabular ${preview.total_penghasilan_vendor >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatRupiah(preview.total_penghasilan_vendor)}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                        <div>
                            <Label>Dicek oleh</Label>
                            <Input value={dicekOleh} onChange={(e) => setDicekOleh(e.target.value)} data-testid="invoice-form-dicek" className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>Disetujui oleh</Label>
                            <Input value={disetujuiOleh} onChange={(e) => setDisetujuiOleh(e.target.value)} data-testid="invoice-form-disetujui" className="mt-1.5 rounded-xl" />
                        </div>
                        <div className="md:col-span-2">
                            <Label>Catatan Admin</Label>
                            <Textarea value={catatanAdmin} onChange={(e) => setCatatanAdmin(e.target.value)} rows={2} className="mt-1.5 rounded-xl" />
                        </div>
                    </div>

                    {preview.total_penghasilan_vendor < 0 && (
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50/60 border border-amber-200">
                            <Checkbox id="carry-over" checked={carryOver} onCheckedChange={setCarryOver} data-testid="invoice-form-carryover" className="mt-0.5" />
                            <label htmlFor="carry-over" className="text-sm text-slate-700 cursor-pointer">
                                <span className="font-semibold">Bawa saldo minus ini ke invoice periode berikutnya</span>
                                <div className="text-xs text-slate-500 mt-0.5">Nilai <span className="font-semibold text-rose-700">{formatRupiah(preview.total_penghasilan_vendor)}</span> akan otomatis muncul sebagai Saldo Awal di invoice vendor berikutnya.</div>
                            </label>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                        {isAdmin ? (
                            <Button onClick={() => setConfirmTutup(true)} data-testid="invoice-tutup-btn" className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl gap-2"><Lock className="w-4 h-4" /> Tutup Invoice</Button>
                        ) : (
                            <div className="text-xs text-slate-500">Hanya admin (RAJAYA) yang bisa menutup invoice.</div>
                        )}
                    </div>
                </div>
            )}

            <AlertDialog open={confirmTutup} onOpenChange={setConfirmTutup}>
                <AlertDialogContent className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl max-w-lg">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Tutup Invoice Permanen</AlertDialogTitle>
                        <AlertDialogDescription>
                            Setelah ditutup, <span className="font-semibold text-rose-700">semua transaksi dalam periode ini akan terkunci permanen</span> dan tidak bisa diedit/dihapus lagi. Kasbon otomatis jadi lunas. Lanjutkan?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={doTutup} disabled={submitting} data-testid="invoice-confirm-tutup-btn" className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl">
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menutup...</> : "Ya, Tutup Invoice"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
